import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import vuei18n from '@/i18n/index';
import { RootState } from '@/store/types';
import { AuthState, Locale } from '@/store/modules/types';
import { LocalesDictionary } from '@/store/modules/i18n';
import { Auth, BirthAuth } from '@/api';
import IdDocType from '@/resources/enum/IdDocType';
import MnDocType from '@/resources/enum/MnDocType';
import GpDocTypePassVaccine from '@/resources/enum/GpDocTypePassVaccine';
import RedirectMode from '@/resources/enum/RedirectMode';
import { Response, KycOrgConfig, ApplicantCustomStyle, GeneralDoc, OptionalDocs } from '@/api/auth';
import RegionCode from '@/resources/enum/RegionCode';
import { PAIDY_ORG_ID } from '@/resources/application';

function createState(): AuthState {
  const state: AuthState = {} as AuthState;
  clearState(state);
  return state;
}

function clearState(state: AuthState): void {
  state.requestURL = '';
  state.requestToken = '';
  state.accessToken = '';
  state.idDocs = [];
  state.kycOrgConfig = {
    id: 0,
    birthAuth: false,
    consignmentText: '',
    orgNames: {} as LocalesDictionary,
    redirectURL: '',
    redirectMode: RedirectMode.AutoOnce,
    checkKYCRequestInformation: false,
    checkTermsOfService: false,
    agreedTermsOfService: false,
    autoShootIDDocTypes: [],
    enableClientTypePC: true,
    enableShrinkFrame: true,
    useErrorRedirectURL: false,
    faceVerificationType: 0,
    code: '',
    enableScamAlert: false
  };
  state.regionCode = RegionCode.NONE;
  state.defaultLanguage = 'ja';
  state.kycRequestID = 0;
  state.applicantID = 0;
  state.kycRequestRedirectURL = '';
  state.kycRequestErrorRedirectURL = '';
  state.applicantCustomStyles = [];
  state.warningMessage = {} as LocalesDictionary;
  state.errorSupplementaryMessage = {} as LocalesDictionary;
  state.requestTimes = 0;
  state.myNumberDocTypes = undefined;
  state.generalDocs = undefined;
  state.optionalDocs = undefined;
  state.pageTransitionMode = 0;
}

export const mutations: MutationTree<AuthState> = {
  storeRequestURL(state, payload: { requestURL: string }): void {
    state.requestURL = payload.requestURL;
  },

  storeRequestToken(state, payload: { requestToken: string }): void {
    state.requestToken = payload.requestToken;
  },

  storeAccessToken(state, payload: { accessToken: string }): void {
    state.accessToken = payload.accessToken;
  },

  storeAuth(state, payload: Response): void {
    state.idDocs = payload.idDocs;
    state.kycOrgConfig = payload.kycOrgConfig;
    state.kycRequestID = payload.kycRequestID;
    state.applicantID = payload.applicantID;
    state.kycRequestRedirectURL = payload.kycRequestRedirectURL;
    state.kycRequestErrorRedirectURL = payload.kycRequestErrorRedirectURL;
    state.applicantCustomStyles = payload.applicantCustomStyles;
    state.warningMessage = payload.warningMessage;
    state.errorSupplementaryMessage = payload.errorSupplementaryMessage;
    state.requestTimes = payload.requestTimes;
    state.myNumberDocTypes = payload.myNumberDocTypes;
    state.generalDocs = payload.generalDocs;
    state.optionalDocs = payload.optionalDocs;
    state.defaultLanguage = payload.defaultLanguage;
    state.regionCode = payload.regionCode;
    state.pageTransitionMode = payload.pageTransitionMode;
  },

  // ClientType指定時に返却される容貌真贋判定タイプ（パッシブ判定）で上書きする
  setKycOrgConfigFaceVerificationType(
    state,
    payload: { faceVerificationType: Response['kycOrgConfig']['faceVerificationType'] }
  ): void {
    state.kycOrgConfig = {
      ...state.kycOrgConfig,
      faceVerificationType: payload.faceVerificationType
    };
  },

  clearOrgNames(state): void {
    state.kycOrgConfig = Object.assign({}, state.kycOrgConfig, {
      orgNames: {}
    });
  },

  clearState(state: AuthState): void {
    clearState(state);
  }
};

export const getters: GetterTree<AuthState, RootState> = {
  requestURL: (state: AuthState): string => state.requestURL,
  requestToken: (state: AuthState): string => state.requestToken,
  accessToken: (state: AuthState): string => state.accessToken,
  idDocTypes: (state: AuthState): IdDocType[] =>
    state.idDocs
      .sort((a, b) => {
        if (a.displayPriority === b.displayPriority) {
          return a.idDocType - b.idDocType;
        }

        return a.displayPriority - b.displayPriority;
      })
      .map((idDoc) => idDoc.idDocType),
  kycOrgConfig: (state: AuthState): KycOrgConfig => state.kycOrgConfig,
  kycOrgConfigBirthAuth: (state: AuthState): boolean => state.kycOrgConfig.birthAuth,
  kycOrgConfigCheckTermsOfService: (state: AuthState): boolean => state.kycOrgConfig.checkTermsOfService,
  kycOrgConfigAgreedTermsOfService: (state: AuthState): boolean => state.kycOrgConfig.agreedTermsOfService,
  kycOrgConfigEnableClientTypePC: (state: AuthState): boolean => state.kycOrgConfig.enableClientTypePC,
  kycOrgConfigEnableShrinkFrame: (state: AuthState): boolean => state.kycOrgConfig.enableShrinkFrame,
  kycOrgConfigFaceVerificationType: (state: AuthState): number => state.kycOrgConfig.faceVerificationType,
  kycRequestID: (state: AuthState): number => state.kycRequestID,
  applicantId: (state: AuthState): number => state.applicantID,
  kycRequestRedirectURL: (state: AuthState): string => state.kycRequestRedirectURL,
  kycRequestErrorRedirectURL: (state: AuthState): string => state.kycRequestErrorRedirectURL,
  applicantCustomStyles: (state: AuthState): ApplicantCustomStyle[] => state.applicantCustomStyles,
  requestTimes: (state: AuthState): number => state.requestTimes,
  myNumberDocTypes: (state: AuthState): MnDocType[] => state.myNumberDocTypes || [],
  generalDocs: (state: AuthState): GeneralDoc[] => state.generalDocs || [],
  optionalDocs: (state: AuthState): OptionalDocs[] => state.optionalDocs || [],
  pageTransitionMode: (state: AuthState): number => state.pageTransitionMode,
  regionCode: (state: AuthState): RegionCode => state.regionCode,
  defaultLanguage: (state: AuthState): Locale => state.defaultLanguage,
  currentLanguageOrgName: (state: AuthState, _, __, rootGetters): string => {
    const locale: Locale = rootGetters['i18n/locale'];
    if (state.kycOrgConfig.orgNames[locale]) return state.kycOrgConfig.orgNames[locale];
    if (state.kycOrgConfig.orgNames.en) return state.kycOrgConfig.orgNames.en;
    return vuei18n.t('general.defaultOrgName') as string;
  },
  currentLanguageWarningMessage: (state: AuthState, _, __, rootGetters): string => {
    const locale: Locale = rootGetters['i18n/locale'];
    if (state.warningMessage[locale]) return state.warningMessage[locale];
    if (state.warningMessage.en) return state.warningMessage.en;
    return '';
  },
  currentLanguageErrorSupplementaryMessage: (state: AuthState, _, __, rootGetters): string => {
    const locale: Locale = rootGetters['i18n/locale'];
    if (state.errorSupplementaryMessage[locale]) return state.errorSupplementaryMessage[locale];
    if (state.errorSupplementaryMessage.en) return state.errorSupplementaryMessage.en;
    return '';
  },
  isCollectMnDoc: (state: AuthState): boolean => {
    return !!state.myNumberDocTypes && state.myNumberDocTypes.length > 0;
  },
  isCollectMnDocOnly: (_0, _getters: { isCollectMnDoc: boolean; idDocTypes: IdDocType[] }): boolean => {
    return _getters.isCollectMnDoc && _getters.idDocTypes.includes(IdDocType.MnGeneral);
  },
  isCollectGpDoc: (state: AuthState): boolean => {
    return !!state.generalDocs && state.generalDocs.length > 0;
  },
  isOnlyGpGeneral: (_0, _getters: { idDocTypes: IdDocType[] }): boolean => {
    return _getters.idDocTypes.every((idDocType) => idDocType === IdDocType.GpGeneral);
  },
  isPassVaccine: (state: AuthState, getters): boolean => {
    // ワクチンモード判定
    if (!getters['isCollectGpDoc']) return false;
    if (!getters['isOnlyGpGeneral']) return false;
    return state.generalDocs!.every((generalDoc: GeneralDoc): boolean => {
      if (generalDoc.generalDocType === GpDocTypePassVaccine.SecondRecord) return true;
      if (generalDoc.generalDocType === GpDocTypePassVaccine.SecondCertificate) return true;
      if (generalDoc.generalDocType === GpDocTypePassVaccine.ThirdCertificate) return true;
      if (generalDoc.generalDocType === GpDocTypePassVaccine.ThirdInoculation) return true;
      return false;
    });
  },
  // 事業者がPaidyのとき
  isPaidy: (state: AuthState): boolean => state.kycOrgConfig.id === PAIDY_ORG_ID,
  showScamAlert: (state: AuthState): boolean => state.kycOrgConfig.enableScamAlert
};

export const actions: ActionTree<AuthState, RootState> = {
  clearState(context: ActionContext<AuthState, RootState>): void {
    context.commit('clearState');
  },

  async storeAuth(context: ActionContext<AuthState, RootState>): Promise<void> {
    return new Promise((resolve) => {
      window.addEventListener('deviceCheckCompleted', async () => {
        // 前段のデバイス判定で取得済みの場合はLocal Storageから取得する
        let cachedAuth: Response | undefined;

        // localstorageから取得してJSON.parse
        try {
          const localStorageItem = localStorage.getItem('liquid-ekyc-applicant-auth');
          if (localStorageItem) {
            cachedAuth = JSON.parse(localStorageItem);
          }
        } catch (e) {
          // パースエラーは握りつぶす
        }

        const auth = cachedAuth ? cachedAuth : await Auth.fetch().then((res) => res.data);
        context.commit('storeAccessToken', { accessToken: auth.accessToken });
        context.commit('storeAuth', auth);
        resolve();
      });
    });
  },

  async storeBirthAuth(
    context: ActionContext<AuthState, RootState>,
    payload: { year: number; month: number; date: string }
  ): Promise<void> {
    const month: string = `00${payload.month}`.slice(-2);
    const date: string = `00${payload.date}`.slice(-2);
    const birthDay: string = `${payload.year}-${month}-${date}`;
    const response = await BirthAuth.fetch({ birthDay });
    context.commit('storeAccessToken', { accessToken: response.data.accessToken });
  },

  setKycOrgConfigFaceVerificationType(
    context: ActionContext<AuthState, RootState>,
    payload: { faceVerificationType: Response['kycOrgConfig']['faceVerificationType'] }
  ): void {
    context.commit('setKycOrgConfigFaceVerificationType', payload);
  }
};

export const auth: Module<AuthState, RootState> = {
  namespaced: true,
  state: createState,
  getters,
  mutations,
  actions
};
